import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Globe, Star, Truck } from "lucide-react";
import ReviewsSection from "@/components/ReviewsSection";
import ReportDialog from "@/components/ReportDialog";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function CompanyDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/removal-companies/${id}`).then(({ data }) => setC(data)).catch(() => navigate("/removal-companies"));
  }, [id, navigate]);

  const requestQuote = async () => {
    if (!user) { navigate("/login"); return; }
    if (!msg.trim()) { toast.error("Add a quick description"); return; }
    if (c.owner_id === "seed") {
      toast.success("Quote request sent (demo partner)");
      setMsg("");
      return;
    }
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: c.owner_id, body: `Quote request:\n${msg}` });
      toast.success("Quote request sent");
      setMsg("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  if (!c) return <div className="mx-auto max-w-7xl px-6 py-16">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <Link to="/removal-companies" className="overline text-muted-foreground hover:text-forest">← Directory</Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8">
        <div>
          <div className="border border-border bg-white p-8 lg:p-12">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-forest text-white grid place-items-center">
                <Truck className="w-7 h-7" />
              </div>
              <div>
                <div className="overline text-safety">Removal company</div>
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  <h1 className="font-display font-black text-4xl tracking-tighter text-forest">{c.name}</h1>
                  <VerifiedBadge verified={c.owner_is_verified} size="lg" testId="company-verified-badge" />
                </div>
              </div>
            </div>
            <p className="mt-6 text-base leading-relaxed text-foreground/90">{c.description}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-border">
              {c.rating > 0 && (
                <div>
                  <div className="overline text-muted-foreground">Rating</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-4 h-4 fill-safety text-safety" />
                    <span className="font-display text-xl tabular-nums">{c.rating.toFixed(1)}</span>
                    <span className="overline text-xs text-muted-foreground">({c.review_count})</span>
                  </div>
                </div>
              )}
              {c.hourly_rate && (
                <div>
                  <div className="overline text-muted-foreground">From</div>
                  <div className="font-display text-xl tabular-nums text-forest mt-1">£{c.hourly_rate}/hr</div>
                </div>
              )}
              <div>
                <div className="overline text-muted-foreground">Services</div>
                <div className="text-sm mt-1">{c.services?.length || 0}</div>
              </div>
              <div>
                <div className="overline text-muted-foreground">Areas</div>
                <div className="text-sm mt-1">{c.service_areas?.length || 0}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 border border-border bg-white p-8">
            <div className="overline text-safety">Services</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {c.services?.map((s) => (
                <span key={s} className="border border-border px-3 py-1.5 text-sm">{s}</span>
              ))}
            </div>

            <div className="overline text-safety mt-8">Service areas</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {c.service_areas?.map((s) => (
                <span key={s} className="border border-border px-3 py-1.5 text-sm">{s}</span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <ReviewsSection
              companyId={c.company_id}
              onAggregateChange={() => api.get(`/removal-companies/${c.company_id}`).then(({ data }) => setC(data))}
            />
          </div>
        </div>

        {/* Sidebar: request quote */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="border border-border bg-white p-6 lg:p-8">
            <div className="overline text-safety">Get a free quote</div>
            <div className="mt-2 font-display text-2xl tracking-tight text-forest">Request a quote</div>
            <p className="mt-2 text-sm text-muted-foreground">Tell them where, when and roughly what.</p>
            <Textarea
              rows={5}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="e.g. Clearing 8th floor Shoreditch office by end of Feb — 60 desks & chairs."
              data-testid="company-quote-input"
              className="mt-4 rounded-none"
            />
            <Button
              onClick={requestQuote}
              disabled={sending}
              data-testid="company-quote-submit"
              className="mt-3 w-full rounded-none bg-safety hover:bg-safety-hover text-white h-12"
            >
              {sending ? "Sending…" : "Request quote"}
            </Button>
          </div>

          <div className="border border-border bg-white p-6">
            <div className="overline text-muted-foreground mb-2">Contact</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-forest" /> {c.phone}</div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-forest" /> {c.email}</div>
              {c.website && <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-forest" /> <a href={c.website} target="_blank" rel="noreferrer" className="text-forest hover:text-safety">{c.website}</a></div>}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <ReportDialog targetType="company" targetId={c.company_id} testId="report-company" label="Report this company" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
